import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink, Package } from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { productService } from '../services/productService';
import { PendingOrderInfo } from '../types/product';

interface PendingOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number | null;
  productSku: string;
  productDescription?: string;
}

const formatDate = (d: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('pt-BR');
};

export const PendingOrdersModal: React.FC<PendingOrdersModalProps> = ({
  isOpen,
  onClose,
  productId,
  productSku,
  productDescription,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pendingOrders, setPendingOrders] = useState<PendingOrderInfo[]>([]);
  const [totalReserved, setTotalReserved] = useState(0);
  const [stockQuantity, setStockQuantity] = useState<number | null>(null);
  const [stockAvailable, setStockAvailable] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !productId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await productService.getPendingOrders(productId);
        if (cancelled) return;
        setPendingOrders(data.pending_orders || []);
        setTotalReserved(data.total_reserved);
        setStockQuantity(data.stock_quantity);
        setStockAvailable(data.stock_quantity_available);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Erro ao carregar pedidos pendentes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, productId]);

  const handleOrderClick = (orderId: number) => {
    onClose();
    navigate(`/sales-orders/${orderId}`);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Pedidos Pendentes — ${productSku}`}
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      }
    >
      {productDescription && (
        <p className="text-sm text-slate-500 mb-3">{productDescription}</p>
      )}

      {/* Stock summary */}
      {stockQuantity != null && stockAvailable != null && (
        <div className="flex gap-4 mb-4 text-sm">
          <div className="bg-slate-50 rounded px-3 py-2">
            <span className="text-slate-500">Est. Físico:</span>{' '}
            <span className="font-semibold text-slate-800">{stockQuantity}</span>
          </div>
          <div className="bg-slate-50 rounded px-3 py-2">
            <span className="text-slate-500">Est. Disponível:</span>{' '}
            <span className={`font-semibold ${stockAvailable < 0 ? 'text-red-600' : stockAvailable === 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {stockAvailable}
            </span>
          </div>
          <div className="bg-slate-50 rounded px-3 py-2">
            <span className="text-slate-500">Reservado:</span>{' '}
            <span className="font-semibold text-orange-600">{totalReserved}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
          <Loader2 size={18} className="animate-spin text-emerald-600" />
          Carregando pedidos pendentes...
        </div>
      ) : error ? (
        <div className="py-6 text-center text-red-500 text-sm">{error}</div>
      ) : pendingOrders.length === 0 ? (
        <div className="py-6 text-center text-slate-400 flex flex-col items-center gap-2">
          <Package size={24} className="text-slate-300" />
          <span>Nenhum pedido pendente reservando este produto</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase">Pedido</th>
                <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase">Data</th>
                <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase">Cliente</th>
                <th className="py-2 px-3 text-right text-xs font-semibold text-slate-500 uppercase">Qtd Reservada</th>
              </tr>
            </thead>
            <tbody>
              {pendingOrders.map(po => (
                <tr key={po.order_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-2 px-3">
                    <button
                      className="text-emerald-600 hover:text-emerald-800 font-medium inline-flex items-center gap-1 hover:underline"
                      onClick={() => handleOrderClick(po.order_id)}
                    >
                      #{po.order_id}
                      <ExternalLink size={12} />
                    </button>
                  </td>
                  <td className="py-2 px-3 text-slate-600">{formatDate(po.order_date)}</td>
                  <td className="py-2 px-3">
                    <span className="inline-block px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-full">
                      {po.order_status || '—'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-600">{po.customer_name || '—'}</td>
                  <td className="py-2 px-3 text-right font-semibold text-orange-600">{po.quantity_reserved}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-200">
              <tr>
                <td colSpan={4} className="py-2 px-3 text-right text-xs font-semibold text-slate-500 uppercase">Total Reservado</td>
                <td className="py-2 px-3 text-right font-bold text-orange-700">{totalReserved}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Dialog>
  );
};
